import React, { useState } from 'react';
import { api } from '../../config/api';
import Notification from '../shared/Notification';
import { StarIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const WordItem = ({ word, onDelete, onUpdate, isSelected, onToggleSelection }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedWord, setEditedWord] = useState({
    original: word.original,
    translation: word.translation,
    notes: word.notes || ''
  });
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const updatedWord = await api.updateWord(word.id, editedWord);
      onUpdate(updatedWord);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas aktualizacji słówka');
    }
  };

  const handleCancel = () => {
    setEditedWord({
      original: word.original,
      translation: word.translation,
      notes: word.notes || ''
    });
    setIsEditing(false);
    setError('');
  };

  const handleToggleFavorite = async () => {
    try {
      const updatedWord = await api.toggleFavorite(word.id);
      onUpdate(updatedWord);
      setNotification({
        message: updatedWord.is_favorite ? 'Słówko zostało dodane do ulubionych' : 'Słówko zostało usunięte z ulubionych',
        type: 'success'
      });
    } catch (err) {
      setNotification({
        message: err.message || 'Wystąpił błąd podczas aktualizacji ulubionych',
        type: 'error'
      });
    }
  };

  const handleClick = (e) => {
    // Nie zaznaczaj, jeśli kliknięto na przycisk akcji
    if (e.target.closest('button')) {
      return;
    }
    onToggleSelection();
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-4 relative group cursor-pointer ${
        isSelected ? 'border-primary-500 bg-blue-50' : 'border-neutral-200'
      }`}
      onClick={handleClick}
    >
      <div className="absolute top-2 right-2 flex items-center space-x-1">
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              className="p-1 text-neutral-400 hover:text-green-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFavorite();
              }}
              className={`opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-neutral-100 transition-all ${
                word.is_favorite ? 'text-yellow-400 hover:text-yellow-500' : 'text-neutral-400 hover:text-yellow-400'
              }`}
            >
              {word.is_favorite ? (
                <StarIconSolid className="h-5 w-5 text-yellow-400" />
              ) : (
                <StarIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-blue-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <PencilIcon className="h-5 w-5 text-gray-400" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(word.id);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-red-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <TrashIcon className="h-5 w-5 text-gray-400" />
            </button>
          </>
        )}
      </div>

      <div className="flex flex-col space-y-3">
        <div className="flex flex-col">
          <div className="flex-1">
            <p className="text-xs text-neutral-500 mb-1">Słówko</p>
            {isEditing ? (
              <input
                type="text"
                value={editedWord.original}
                onChange={(e) => setEditedWord({ ...editedWord, original: e.target.value })}
                className="w-full p-1 text-sm font-medium text-neutral-800 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            ) : (
              <p className="text-sm font-medium text-neutral-800">{word.original}</p>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs text-neutral-500 mb-1">Tłumaczenie</p>
          {isEditing ? (
            <input
              type="text"
              value={editedWord.translation}
              onChange={(e) => setEditedWord({ ...editedWord, translation: e.target.value })}
              className="w-full p-1 text-sm font-medium text-neutral-800 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          ) : (
            <p className="text-sm font-medium text-neutral-800">{word.translation}</p>
          )}
        </div>

        {word.notes && (
          <div className="mt-2 pt-2 border-t border-neutral-100">
            {isEditing ? (
              <textarea
                value={editedWord.notes}
                onChange={(e) => setEditedWord({ ...editedWord, notes: e.target.value })}
                className="w-full p-1 text-xs text-neutral-500 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="2"
              />
            ) : (
              <p className="text-xs text-neutral-500 line-clamp-2">{word.notes}</p>
            )}
          </div>
        )}

        {word.tags && word.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {word.tags.map((tag) => (
              <span
                key={tag.id}
                className="px-1.5 py-0.5 text-xs text-white rounded"
                style={{ backgroundColor: tag.color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 p-2 text-xs text-red-600 bg-red-50 rounded">
          {error}
        </div>
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default WordItem; 